import { Injectable, inject } from '@angular/core';
import { Borders, Workbook, Worksheet } from 'exceljs';
import jsPDF from 'jspdf';
import autoTable, { Styles } from 'jspdf-autotable';
import { reportConstants, reportConstantsMessage } from '../../common/constants';
import { ParaffinReports } from '../../models/lab-test';
import { FractionComponent, FractionReports } from '../../models/project';
import { ParaffinWeights } from '../../paraffin/models/paraffin';
import { ReportGeneralUtilityService } from '../report.generalUtility.service';
import { setNumericOrTextCellValue } from './report.cellFormat.util';

@Injectable({
    providedIn: 'root'
})
export class ReportPDFParaffinRenderService {
    private _reportGeneralUtilityService = inject(ReportGeneralUtilityService);

    private commonStylesWithoutBorder = {
        fontSize: 8,
        textColor: [0, 0, 0],
        fillColor: false,
        lineColor: [0, 0, 0],
        lineWidth: 0
    };

    ParaffinPage(doc: jsPDF, marginSize: number, paraffinExp: ParaffinReports, appendix = '', wellNameVersion: string, sampleNameVersion: string): void {
        if (paraffinExp !== undefined && paraffinExp !== null) {
            doc.addPage();
            let tableStartY = marginSize;

            doc.setFontSize(14);
            doc.setFont(reportConstants.eMprintCondensedBold);
            doc.text(appendix + reportConstants.nParaffinAnalysis, marginSize - 7, tableStartY);
            tableStartY += 5;

            doc.setFontSize(10);
            doc.setFont(reportConstants.eMprintCondensedRegular);
            doc.text(wellNameVersion + ' ' + sampleNameVersion, marginSize - 7, tableStartY);

            tableStartY += 5;
            doc.text(appendix + reportConstants.singleStageFlashResidualOil, marginSize - 7, tableStartY);
            tableStartY += 5;
            autoTable(doc, {
                head: [
                    [reportConstants.nParaffin, reportConstants.weight]
                ],
                body: paraffinExp.paraffinData.map((val: ParaffinWeights) => [val.paraffinCode ?? '', val.weightPercentage?.toFixed(3) ?? null]),
                startY: Math.round(tableStartY),
                theme: reportConstants.plain,
                margin: { left: marginSize + 50, right: marginSize + 0 },
                styles: this.commonStylesWithoutBorder as Partial<Styles>,
                headStyles: {
                    fontSize: 10,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    fontStyle: reportConstants.normal,
                    font: reportConstants.eMprintCondensedBold,
                    cellPadding: { top: 0.45, bottom: 0.45 },
                    lineWidth: 0
                },
                bodyStyles: {
                    fontSize: 8,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    font: reportConstants.eMprintCondensedRegular,
                    cellPadding: { top: 0.45, bottom: 0.45 },
                    lineWidth: 0
                },
                columnStyles: {
                    0: { cellWidth: 30, font: reportConstants.eMprintCondensedBold },
                    1: { cellWidth: 30 }
                },
                didDrawCell: function (data) {
                    const { cell, row, doc, section } = data;
                    if (section === reportConstants.head) {
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;

                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);

                        doc.line(x, y, x + w, y);
                        doc.line(x, y, x, y + h);
                        doc.line(x + w, y, x + w, y + h);

                        doc.line(x, y + h - 0.5, x + w, y + h - 0.5);
                        doc.line(x, y + h, x + w, y + h);

                        if (row.index === 0) {
                            doc.line(x, y, x + w, y);
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        } else if (row.index === 3) {
                            doc.line(x, y + h - 0.5, x + w, y + h - 0.5);
                            doc.line(x, y + h, x + w, y + h);
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        } else {
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        }
                    } else {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        doc.line(x, y, x, y + h);
                        doc.line(x + w, y, x + w, y + h);
                        doc.line(x, y + h, x + w, y + h);
                    }
                }
            });
        }
    }

    async GenerateNParaffinAnalysis(workbook: Workbook, worksheetName: string, paraffinExp: ParaffinReports, wellName: string, sampleName: string, subHeader: string) {
        try {
            const nParaffinWorksheet = workbook.addWorksheet(worksheetName);

            const paraffinWeights = paraffinExp.paraffinData ?? [];

            const borderStyle = {
                top: { style: reportConstants.thin },
                left: { style: reportConstants.thin },
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            nParaffinWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(nParaffinWorksheet, undefined, {
                paperSize: undefined, // letter
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: true,
                fitToWidth: 1,
                fitToHeight: 1,
                scale: 50,
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
            });
            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            nParaffinWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(nParaffinWorksheet, subHeader, reportConstants.c1, reportConstants.i1);

            let rowNo = 3;
            nParaffinWorksheet.getRow(rowNo).height = 30;
            nParaffinWorksheet.mergeCells(reportConstants.c + rowNo, reportConstants.e + (rowNo + 1));
            const textNParaffinAnalysisCell = nParaffinWorksheet.getCell(reportConstants.c + rowNo);
            textNParaffinAnalysisCell.value = reportConstants.nParaffinAnalysis;
            textNParaffinAnalysisCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textNParaffinAnalysisCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            rowNo += 2;
            nParaffinWorksheet.getRow(rowNo).height = 24;
            nParaffinWorksheet.mergeCells(reportConstants.c + rowNo, reportConstants.e + rowNo);
            const textWellAndSampleNameCell = nParaffinWorksheet.getCell(reportConstants.c + rowNo);
            textWellAndSampleNameCell.value = wellName + ' ' + sampleName;
            textWellAndSampleNameCell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
            textWellAndSampleNameCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            rowNo++;
            nParaffinWorksheet.getRow(rowNo).height = 24;
            nParaffinWorksheet.mergeCells(reportConstants.c + rowNo, reportConstants.e + rowNo);
            const textReportTitleCell = nParaffinWorksheet.getCell(reportConstants.c + rowNo);
            textReportTitleCell.value = reportConstants.singleStageFlashResidualOil;
            textReportTitleCell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
            textReportTitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            const tableHeader = [reportConstants.nParaffin, reportConstants.weight];
            const tableBody = paraffinWeights.map((val: ParaffinWeights) => [
                val.paraffinCode?.toString() ?? '',
                val.weightPercentage?.toFixed(3)?.toString() ?? ''
            ]);

            let tableStartRow = rowNo + 2;
            const tableStartColumn = 4;

            nParaffinWorksheet.getColumn(tableStartColumn).width = 15;
            nParaffinWorksheet.getColumn(tableStartColumn + 1).width = 15;

            tableHeader.forEach((header, colIdx) => {
                const cell = nParaffinWorksheet.getCell(tableStartRow, tableStartColumn + colIdx);
                cell.value = header;
                cell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
                cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                cell.border = {
                    top: borderStyle.top,
                    left: borderStyle.left,
                    right: borderStyle.right,
                    bottom: { style: reportConstants.double }
                } as Partial<Borders>;
            });
            nParaffinWorksheet.getRow(tableStartRow).height = 20;

            tableStartRow++;
            tableBody.forEach((row: (string | number)[], rowIdx: number) => {
                row.forEach((cellValue, colIdx) => {
                    const cell = nParaffinWorksheet.getCell(tableStartRow + rowIdx, tableStartColumn + colIdx);
                    setNumericOrTextCellValue(cell, cellValue);
                    cell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: (colIdx === 0) };
                    cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                    cell.border = borderStyle as Partial<Borders>;
                });
                nParaffinWorksheet.getRow(tableStartRow + rowIdx).height = 20;
            });

            const endRowNo = tableStartRow + tableBody.length;
            this.AddOuterBorder(nParaffinWorksheet, 3, endRowNo, 1, 7);

            this.SetDefaultCellStyles(nParaffinWorksheet, endRowNo + 5, 12);
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }

    }

    async GenerateFractionAppendix(
        workbook: Workbook,
        worksheetName: string,
        fractionExp: FractionReports,
        subHeader: string
    ) {
        try {
            if (!fractionExp || !fractionExp.components) return;

            const worksheet = workbook.addWorksheet(worksheetName, {
                views: [{ showGridLines: false }]
            });
            const appendixWorksheet = worksheet;
            appendixWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(appendixWorksheet, undefined, {
                paperSize: undefined, // letter
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: true,
                fitToWidth: 1,
                fitToHeight: 1,
                scale: 45,
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                },
            });

            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            appendixWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(appendixWorksheet, subHeader, reportConstants.c1, reportConstants.h1);

            this.SetDefaultCellStyles(appendixWorksheet, 70, 17);
            appendixWorksheet.getColumn(reportConstants.a).width = 5;
            appendixWorksheet.getColumn(reportConstants.b).width = 10;
            appendixWorksheet.getColumn(reportConstants.c).width = 25;
            appendixWorksheet.getColumn(reportConstants.d).width = 4;
            appendixWorksheet.getColumn(reportConstants.e).width = 18;
            appendixWorksheet.getColumn(reportConstants.f).width = 30;

            const titleCell = appendixWorksheet.getCell(reportConstants.b3);
            titleCell.value = reportConstants.fractionAppendix;
            titleCell.font = { name: reportConstants.eMprintCondensedBold, size: 12, bold: true };
            titleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            let rowNo = 5;

            appendixWorksheet.mergeCells(`B${rowNo}:C${rowNo}`);
            const headerRow = appendixWorksheet.getRow(rowNo++);
            const headers = reportConstants.headersFraction;
            const headerCols = [2, 4, 5, 6];

            headers.forEach((text, idx) => {
                const col = headerCols[idx];
                const cell = headerRow.getCell(col);
                cell.value = text;
                cell.font = { name: reportConstants.eMprintCondensedBold, size: 12, bold: true };
                cell.alignment = { horizontal: reportConstants.center };
                cell.border = {
                    top: { style: reportConstants.thin },
                    bottom: { style: reportConstants.double },
                    left: { style: reportConstants.thin },
                    right: { style: reportConstants.thin }
                };
            });
            headerRow.commit();

            fractionExp.components.forEach((val: FractionComponent) => {
                const row = appendixWorksheet.getRow(rowNo++);
                row.getCell(2).value = val.component;
                row.getCell(3).value = val.componentName;
                row.getCell(4).value = val.StarsCol;
                setNumericOrTextCellValue(row.getCell(5), val.mw);
                setNumericOrTextCellValue(row.getCell(6), val.density);

                [2, 3, 4].forEach((col) => {
                    const cell = row.getCell(col);
                    cell.font = { name: reportConstants.eMprintCondensedBold, size: 12, bold: true };
                    cell.alignment = { horizontal: reportConstants.center };
                    cell.border = {
                        top: { style: reportConstants.thin },
                        bottom: { style: reportConstants.thin },
                        left: { style: reportConstants.thin },
                        right: { style: reportConstants.thin }
                    };
                });

                [5, 6].forEach((col) => {
                    const cell = row.getCell(col);
                    cell.font = { name: reportConstants.eMprintCondensedRegular, size: 12 };
                    cell.alignment = { horizontal: reportConstants.center };
                    cell.border = {
                        top: { style: reportConstants.thin },
                        bottom: { style: reportConstants.thin },
                        left: { style: reportConstants.thin },
                        right: { style: reportConstants.thin }
                    };
                });

                row.commit();
            });

            const footnotes = reportConstants.footnotesFraction;

            rowNo += 1;
            footnotes.forEach((note) => {
                const row = appendixWorksheet.getRow(rowNo++);

                const cell = row.getCell(2);
                cell.value = note;
                cell.font = { name: reportConstants.eMprintCondensedRegular, size: 8 };

                row.commit();
            });
        } catch (error) {
            console.error(reportConstantsMessage.errorGeneratingFractionAppendixSheet, error);
        }
    }

    private SetDefaultCellStyles(worksheet: Worksheet, rowCount: number, columnCount: number, rowHeight = 18) {
        for (let i = 1; i <= rowCount; i++) {
            const row = worksheet.getRow(i);
            row.height = rowHeight;
            for (let j = 1; j <= columnCount; j++) {
                const cell = row.getCell(j);
                cell.fill = {
                    type: reportConstants.pattern,
                    pattern: reportConstants.solid,
                    fgColor: { argb: reportConstants.ffffffff }
                };
            }
        }
    }

    public AddOuterBorder(
        worksheet: Worksheet,
        startRowNo: number,
        endRowNo: number,
        startColumnNo: number,
        endColumnNo: number
    ) {
        for (let r = startRowNo; r <= endRowNo; r++) {
            for (let c = startColumnNo; c <= endColumnNo; c++) {
                const cell = worksheet.getCell(r, c);
                const currentTop = cell.border && cell.border.top ? cell.border.top : undefined;
                const currentBottom = cell.border && cell.border.bottom ? cell.border.bottom : undefined;
                const currentLeft = cell.border && cell.border.left ? cell.border.left : undefined;
                const currentRight = cell.border && cell.border.right ? cell.border.right : undefined;

                cell.border = {
                    top: r === startRowNo ? { style: reportConstants.thin } : currentTop,
                    bottom: r === endRowNo ? { style: reportConstants.thin } : currentBottom,
                    left: c === startColumnNo ? { style: reportConstants.thin } : currentLeft,
                    right: c === endColumnNo ? { style: reportConstants.thin } : currentRight,
                };
            }
        }
    }
}